import { useMutation } from '@apollo/client';
import { atom, useAtom, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useRightPanel } from '../../app/layout/RightPanel';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import PopOver from '../../components/deprecated/PopOver';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import {
  BEACON_POST_OVERFLOW_MENU,
  BEACON_POST_VIEW_HISTORY,
} from '../../constants/onboarding';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostType, UserType } from '../../graphql/generated/graphql';
import archivePostIcon from '../../icons/non-prefilled/16x16/archive.svg';
import columnsIcon from '../../icons/non-prefilled/16x16/columns.svg';
import copyIcon from '../../icons/non-prefilled/16x16/copy.svg';
import archiveDraftIcon from '../../icons/non-prefilled/16x16/draftArchived.svg';
import editTopicsIcon from '../../icons/non-prefilled/16x16/editTopics.svg';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import imageIcon from '../../icons/non-prefilled/16x16/image.svg';
import informationIcon from '../../icons/non-prefilled/16x16/information.svg';
import memberIcon from '../../icons/non-prefilled/16x16/member.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import AddSeriesModal from '../../series/modal/AddSeriesModal';
import { usePostSeries } from '../../series/useAllSeries';
import TemplatesMenuItem from '../../templates/TemplatesMenuItem';
import ImportMenuButton from '../../topbar/ImportMenuButton';
import { UIActions } from '../../ui/uiSlice';
import download from '../../utils/network/download';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import useMainContentHistory from '../../utils/router/useMainContentHistory';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';
import { isAddOrShareMenuOpenAtom } from '../controlBar/controlBarMenuAtoms';
import CreatePostMenuItem from '../CreatePostMenuItem';
import PostInsights from '../PostInsights';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import { usePostPermissionSubscription } from '../sharing/postPermissionOperations';
import SharingDropdown from '../sharing/SharingDropdown';
import CopyEmbedMenuItem from '../topbar/CopyEmbedMenuItem';
import SidebarMenuItem from '../topbar/SidebarMenuItem';
import TemplatizeMenuItem from '../topbar/TemplatizeMenuItem';
import useAddCover from '../useAddCover';
import useCanDisplayNewPostHistoryView from '../useCanDisplayNewPostHistoryView';
import useDeletePost from '../useDeletePost';
import usePublishPost from '../usePublishPost';
import useToggleArchived from '../useToggleArchived';
import VerifyMenuItems, {
  useShouldRenderVerifyMenuItems,
} from '../VerifyMenuItems';
import * as styles from './PostEditButton.module.scss';

const DUPLICATE_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation duplicatePost($id: ID!) {
    post: createPost(templateId: $id) {
      id
      shareSecret
      ...postPath_post
    }
  }
`);

const POST_EDIT_BUTTON_POST_FRAGMENT = gql(`
  fragment PostEditButton_post on Post {
    id
    publishedAt
    ...usePostEditButtonMenu_post
  }
`);

const USE_POST_EDIT_BUTTON_MENU_POST_FRAGMENT = gql(`
  fragment usePostEditButtonMenu_post on Post {
      id
      publishedAt
      archivedAt
      editedAt
      insertedAt
      externalPost {
        id
      }
      banner {
        original
      }
      topics {
        id
      }
      ...usePostPolicyCheck_post
      ...VerifyMenuItems_post
      ...postPath_post
      ...CopyEmbedMenuItem_post
      ...TemplatizeMenuItem_post
  }
`);

type UsePostEditButtonMenuOption = {
  post: DocumentType<typeof USE_POST_EDIT_BUTTON_MENU_POST_FRAGMENT>;
  onOpenShareDropdown?: () => void;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const usePostEditButtonMenu = ({
  post,
  onOpenShareDropdown,
}: UsePostEditButtonMenuOption) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mainContentHistory = useMainContentHistory();

  const postTopicIds = post.topics.map(({ id }) => id);
  const postSeries = usePostSeries(post.id);

  const isMobile = useIsSize(Sizes.SM);
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isEditable = useAppSelector((state) => state.editor.editable);

  const publishPost = usePublishPost(post.id);
  const toggleArchived = useToggleArchived();

  const deletePost = useDeletePost();
  const [duplicatePost] = useMutation(DUPLICATE_POST_MUTATION);

  const postPolicyCheck = usePostPolicyCheck();
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();
  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);
  const onAddCover = useAddCover();

  const { id, title, publishedAt, externalPost, archivedAt, banner } = post;

  const onToggleArchive = () => {
    toggleArchived(post);
  };

  const onDuplicate = async () => {
    const response = await duplicatePost({ variables: { id } });
    const newPost = response.data?.post;

    if (post.type === PostType.Template) {
      dispatch(UIActions.addUsedTemplate(post.id));
    }

    if (newPost) {
      navigate(
        postPath(newPost, { shareSecret: newPost.shareSecret ?? undefined }),
      );
    }
  };

  const onExport = (type: string, extension: string) => {
    download(
      `/posts/${id}/export/${type}`,
      `${title} - Slab.${extension}`,
    ).catch(() => {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message:
            'Oops, we could not export this post. Please contact support for assistance.',
        }),
      );
    });
  };

  const handleDelete = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Delete this post?',
        message: 'Are you sure you want to delete this post?',
        confirmButtonText: 'Delete',
        onConfirm: async () => {
          await deletePost(id);
          mainContentHistory.back(
            ({ pathname }, index) => {
              const isPreviousLocation = index === 1;
              return (
                isPreviousLocation &&
                !pathname.includes(post.id) &&
                pathname !== '/'
              );
            },
            { fallback: publishedAt ? '/' : '/drafts' },
          );
        },
        width: 447,
      }),
    );
  };

  const canEditPost = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const isPostPublishDisabled =
    currentUser.type === UserType.Guest && postTopicIds.length === 0;

  const archiveIcon = publishedAt ? archivePostIcon : archiveDraftIcon;

  const lifeCycleMenuItems = [
    shouldRenderVerifyMenuItems({ post, user: currentUser }) && (
      <VerifyMenuItems key="verify" post={post} />
    ),
    canEditPost && !externalPost && !archivedAt && (
      <React.Fragment key="publish">
        {publishedAt ? (
          <MenuItem
            key="unpublish"
            icon={unpublishIcon}
            onClick={() => publishPost(false)}
          >
            Unpublish
          </MenuItem>
        ) : (
          <MenuItem
            key="publish"
            icon={publishIcon}
            onClick={() => publishPost(true)}
            disabled={isPostPublishDisabled}
            tooltip={
              isPostPublishDisabled
                ? 'Guests must publish\nposts in at least\none topic'
                : undefined
            }
          >
            Publish
          </MenuItem>
        )}
      </React.Fragment>
    ),
    <SidebarMenuItem key="sidebarItem" item={post} />,
    canEditPost && (
      <MenuItem
        key="archive"
        icon={archivedAt ? unarchiveIcon : archiveIcon}
        onClick={onToggleArchive}
      >
        {archivedAt ? 'Unarchive' : 'Archive'}
      </MenuItem>
    ),
  ].filter(Boolean);

  const { openRightPanel } = useRightPanel();

  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  return (
    <>
      <CreatePostMenuItem />
      <TemplatesMenuItem />
      <ImportMenuButton />
      <MenuSeparator />
      {!externalPost && canDisplayNewPostHistoryView && (
        <TutorialBeacon id={BEACON_POST_VIEW_HISTORY} placement="left">
          {(beaconProps) => (
            <MenuItem
              {...beaconProps}
              icon={historyIcon}
              to={postPath(post, {
                histories: 'last',
              })}
              disabled={post.editedAt <= post.insertedAt}
            >
              View history
            </MenuItem>
          )}
        </TutorialBeacon>
      )}
      <MenuItem
        icon={columnsIcon}
        onClick={() => {
          openRightPanel(<PostInsights id={id} />);
        }}
      >
        View insights
      </MenuItem>
      {onOpenShareDropdown && (
        <MenuItem onClick={onOpenShareDropdown} icon={memberIcon}>
          Share post
        </MenuItem>
      )}
      {!isMobile && (
        <>
          {banner?.original != null ? (
            <MenuItem
              icon={imageIcon}
              onClick={() => {
                dispatch(EditorActions.setIsPostBannerPopOverOpen(true));
              }}
              disabled={!canEditPost}
            >
              Edit cover
            </MenuItem>
          ) : (
            <MenuItem
              icon={imageIcon}
              onClick={onAddCover}
              disabled={!canEditPost}
            >
              Add cover
            </MenuItem>
          )}
        </>
      )}

      {(isEditable || (canEditPost && !!externalPost)) && (
        <MenuItem
          icon={editTopicsIcon}
          onClick={() => {
            if (readOnly) {
              dispatch(EditorActions.readOnly(false));
            }
            // Timeout is needed when the editor changes to editing state to focus the topic input.
            setTimeout(() => {
              setIsAddOrShareMenuOpen(true);
            }, 1);
          }}
        >
          Edit topics
        </MenuItem>
      )}
      {!postSeries && !!publishedAt && canEditPost && !archivedAt && (
        <MenuItem
          icon={seriesIcon}
          onClick={() =>
            dispatch(UIActions.openModal(AddSeriesModal, { post }))
          }
        >
          Add to series
        </MenuItem>
      )}
      {!archivedAt && !externalPost && <TemplatizeMenuItem post={post} />}
      <MenuItem icon={copyIcon} onClick={onDuplicate}>
        {post.type === PostType.Template ? 'Use template' : 'Make a copy'}
      </MenuItem>
      <CopyEmbedMenuItem post={post} />
      <MenuItem
        icon={newTabIcon}
        submenuItems={
          <>
            <MenuItem onClick={() => onExport('docx', 'docx')}>Docx</MenuItem>
            <MenuItem onClick={() => onExport('markdown', 'md')}>
              Markdown
            </MenuItem>
            <MenuItem
              onClick={() => {
                window.print();
              }}
            >
              Print to PDF
              <span className={styles.tooltip}>
                <Tooltip
                  content={
                    'Select PDF as the\nprinter destination\nto export as PDF'
                  }
                  placement="bottom"
                  display="inline"
                >
                  <SVGIcon className={styles.icon} inline>
                    {informationIcon}
                  </SVGIcon>
                </Tooltip>
              </span>
            </MenuItem>
          </>
        }
      >
        Export as
      </MenuItem>
      {lifeCycleMenuItems.length > 0 && (
        <>
          <MenuSeparator />
          {lifeCycleMenuItems}
        </>
      )}
      {!externalPost && canEditPost && (
        <>
          <MenuSeparator />
          <MenuItem danger icon={trashIcon} onClick={handleDelete}>
            Delete
          </MenuItem>
        </>
      )}
    </>
  );
};

export const isShareDropdownOpenAtom = atom(false);

interface PostEditButtonProps {
  post: DocumentType<typeof POST_EDIT_BUTTON_POST_FRAGMENT>;
  mobile?: boolean;
}

const PostEditButton = ({ post, mobile }: PostEditButtonProps) => {
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useAtom(
    isShareDropdownOpenAtom,
  );

  usePostPermissionSubscription(post.id);

  const menu = usePostEditButtonMenu({
    post,
    onOpenShareDropdown: mobile
      ? () => setIsShareDropdownOpen(true)
      : undefined,
  });

  return (
    <div className={styles.container}>
      <PopOver
        open={isShareDropdownOpen}
        onOpenChange={(open) => {
          setIsShareDropdownOpen(open);
        }}
        preventCloseOnContentClick
        popOver={
          <SharingDropdown
            close={() => {
              setIsShareDropdownOpen(false);
            }}
            postId={post.id}
          />
        }
        trigger="click"
        placement="bottom-end"
        responsivePlacement="full"
      >
        {!mobile && (
          <Button variant="secondary" className={styles.shareButton}>
            Share
          </Button>
        )}
      </PopOver>
      <div className={styles.overflowMenu}>
        <OverflowMenu
          beaconId={BEACON_POST_OVERFLOW_MENU}
          beaconPlacement="bottom"
          open={isEditMenuOpen}
          onOpenChange={setIsEditMenuOpen}
          ariaLabel="Post options"
          iconSize={20}
        >
          {menu}
        </OverflowMenu>
      </div>
    </div>
  );
};

export default PostEditButton;
